import { Link } from 'react-router';
import { BreadcrumbSchema, SEO } from '@/components/seo/seo';

export function AboutPage() {
  return (
    <div className="relative">
      <SEO
        title="About Fixtyer | Company, Methodology and AI Transparency"
        description="Who operates Fixtyer, what the product does, how public road-maintenance content is prepared, and how to request a correction."
        canonical="/about"
      />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'About', url: '/about' }]} />

      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-24">
        <div className="absolute inset-x-0 top-0 h-8 bg-[repeating-linear-gradient(-45deg,#f97316,#f97316_40px,#1e40af_40px,#1e40af_80px)]" />
        <div className="relative mx-auto max-w-4xl px-4 pt-8 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center gap-2 text-sm text-brand-600">
            <Link to="/" className="hover:text-brand-700">Home</Link>
            <span aria-hidden="true">/</span>
            <span className="text-surface-700">About</span>
          </div>
          <h1 className="font-display text-4xl font-bold text-brand-950 sm:text-5xl">
            About Fixtyer
          </h1>
          <p className="mt-6 max-w-3xl text-xl leading-relaxed text-surface-700">
            Fixtyer is a self-service document-preparation platform for UK pothole damage claims.
            We help customers organise evidence and prepare documents; customers review, submit and
            manage their own claims.
          </p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-4xl space-y-12 px-4 sm:px-6 lg:px-8">
          <AboutSection title="Company and regulatory details">
            <p>
              Fixtyer is operated by <strong>Elevyn Technology Group Limited</strong> (company number{' '}
              <strong>16954601</strong>). The registered office is 167-169 Great Portland Street, London, England, W1W 5PF.
            </p>
            <p>
              The company&apos;s ICO registration is <strong>ZC078522</strong>, and Fixtyer is an
              ICO-listed trading name.
            </p>
            <div className="flex flex-wrap gap-3">
              <ExternalLink href="https://find-and-update.company-information.service.gov.uk/company/16954601">
                View Companies House record
              </ExternalLink>
              <ExternalLink href="https://ico.org.uk/ESDWebPages/Entry/ZC078522">
                View ICO register entry
              </ExternalLink>
            </div>
          </AboutSection>

          <AboutSection title="What Fixtyer does">
            <p>
              The currently available paid product is the £35 Council Claim Pack. Fixtyer prepares
              documents, organises customer-provided evidence, and supplies general process
              information. The customer checks the output, submits it to the responsible authority,
              responds to that authority, and manages the claim.
            </p>
            <p>
              Fixtyer is not a law firm, does not provide legal advice or representation, and does
              not guarantee outcomes. An £89 Court Pack is planned but is not currently available
              to purchase.
            </p>
          </AboutSection>

          <AboutSection title="Data and editorial methodology">
            <p>
              Council reports and ratings pages use public sources, including Department for
              Transport road-maintenance ratings, council transparency reports, published highway
              policies and public defect-reporting data. Source links are included where practical
              so readers can check the underlying material.
            </p>
            <p>
              The Elevyn Technology Group Limited team is accountable for Fixtyer&apos;s editorial
              content. The team aims to distinguish source facts from explanation, preserve source
              dates and context, and update material when reliable corrections or newer public data
              become available. DfT ratings are network-level context: they are not proof of
              liability and do not automatically defeat a Section 58 defence.
            </p>
          </AboutSection>

          <AboutSection title="AI transparency">
            <p>
              Fixtyer uses AI-assisted features to analyse customer-provided evidence, flag missing
              or inconsistent information, refine draft wording, and organise documents. AI may
              also assist the team with research and drafting for public content.
            </p>
            <p>
              AI output can be incomplete or wrong. Customers must review their documents before
              use, and the Elevyn Technology Group Limited team remains accountable for published
              editorial content. AI does not submit or manage claims and is not a substitute for
              advice from a qualified solicitor.
            </p>
          </AboutSection>

          <AboutSection title="Corrections and contact">
            <p>
              To report a factual error, provide a newer primary source, or ask about this
              methodology, email{' '}
              <a href="mailto:hello@fixtyer.co.uk" className="font-medium text-brand-600 underline">
                hello@fixtyer.co.uk
              </a>{' '}
              with the page URL and supporting source. Privacy enquiries can be sent to{' '}
              <a href="mailto:privacy@fixtyer.co.uk" className="font-medium text-brand-600 underline">
                privacy@fixtyer.co.uk
              </a>.
            </p>
          </AboutSection>
        </div>
      </section>
    </div>
  );
}

function AboutSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-semibold text-brand-950">{title}</h2>
      <div className="mt-4 space-y-4 leading-relaxed text-surface-700">{children}</div>
    </section>
  );
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-h-11 items-center rounded-lg border border-brand-200 px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-50"
    >
      {children}
    </a>
  );
}
